import { computed, ref } from 'vue';
import PostSaleCaseNotificationsAPI from 'dashboard/api/postSaleCaseNotifications';

export function usePostSaleCaseNotifications({ notify, t }) {
  const filter = ref('all');
  const loading = ref(false);
  const error = ref('');
  const rows = ref([]);
  const page = ref(0);
  const totalPages = ref(0);
  const unreadCount = ref(0);

  const hasMore = computed(() => page.value < totalPages.value);

  const load = async ({ reset = true } = {}) => {
    const nextPage = reset ? 1 : page.value + 1;
    if (loading.value || (!reset && !hasMore.value)) return;

    loading.value = true;
    error.value = '';
    try {
      const { data } = await PostSaleCaseNotificationsAPI.list({
        filter: filter.value === 'unread' ? 'unread' : undefined,
        page: nextPage,
      });
      rows.value = reset
        ? data.data || []
        : [...rows.value, ...(data.data || [])];
      page.value = data.meta?.page || nextPage;
      totalPages.value = data.meta?.total_pages || 0;
      unreadCount.value = data.meta?.unread_count || 0;
    } catch (requestError) {
      error.value =
        requestError?.response?.data?.message ||
        t('POST_SALE.NOTIFICATIONS.LOAD_ERROR');
      notify?.(error.value);
    } finally {
      loading.value = false;
    }
  };

  const selectFilter = async value => {
    filter.value = value;
    await load();
  };

  const markRead = async notification => {
    if (!notification || notification.read) return true;

    const index = rows.value.findIndex(row => row.id === notification.id);
    if (index < 0) return false;
    const previous = rows.value[index];
    rows.value[index] = {
      ...previous,
      read: true,
      read_at: new Date().toISOString(),
    };
    unreadCount.value = Math.max(0, unreadCount.value - 1);

    try {
      await PostSaleCaseNotificationsAPI.markRead(notification.id);
      return true;
    } catch (requestError) {
      rows.value[index] = previous;
      unreadCount.value += 1;
      notify?.(
        requestError?.response?.data?.message ||
          t('POST_SALE.NOTIFICATIONS.READ_ERROR')
      );
      return false;
    }
  };

  return {
    error,
    filter,
    hasMore,
    load,
    loading,
    markRead,
    rows,
    selectFilter,
    unreadCount,
  };
}
